import { Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { RouterOutlet } from '@angular/router';
import { ThemeService } from './shared/services/theme.service';
import { TranslationService } from './shared/services/translation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SessionService } from './shared/services/session.service';
import { distinctUntilChanged, finalize, from, map, switchMap, tap } from 'rxjs';
import { StreakStateService } from './shared/services/streak-state.service';
import { StorageAccessService } from './shared/services/storage-access.service';
import { SubmitQueueService } from './shared/services/submit-queue.service';
import { HistoryService } from './shared/services/history.service';
import { BridgeService } from './shared/services/bridge.service';

@UntilDestroy()
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'streaks-fe';

  private readonly theme = inject(ThemeService);
  private readonly translation = inject(TranslationService);
  private readonly session = inject(SessionService);
  private readonly state = inject(StreakStateService);
  private readonly submitQueue = inject(SubmitQueueService);
  private readonly storageAccess = inject(StorageAccessService);
  private readonly history = inject(HistoryService);
  private readonly bridge = inject(BridgeService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    this.setupSubmitSafetyNet();
    void this.storageAccess.ensure();

    const params = this.validateURLParams();

    if (!params) {
      this.removeGlobalLoader();
      return;
    }

    this.state
      .load(params.gameId)
      .pipe(
        switchMap((state) =>
          from(this.bridge.prepare()).pipe(map(() => state)),
        ),
        tap(() => {
          this.state.loadWheelSegments().subscribe();
          this.history.load(params.gameId).subscribe();
        }),
        switchMap((state) => {
          const { streaksThemeUrl, wheelThemeUrl, hubThemeUrl } = state.themes;
          this.translation.loadLabels(state.labels?.streaksThemeUrl).subscribe();
          return this.theme.loadThemes({
            streaksThemeUrl,
            wheelThemeUrl,
            hubThemeUrl,
          });
        }),
        finalize(() => {
          this.removeGlobalLoader();
        }),
      )
      .subscribe();

    this.setupBreakpointObserver();
  }

  private validateURLParams(): { token: string; gameId: string } | null {
    const params = new URLSearchParams(window.location.search);

    const token = params.get('token');
    const gameId = params.get('gameId');

    if (!token || !gameId) {
      const missing: string[] = [];

      if (!token) missing.push('token');
      if (!gameId) missing.push('gameId');

      this.errorMessage.set(`${missing.join(' and ')} not found in URL`);

      return null;
    }

    return {
      token,
      gameId,
    };
  }

  private setupSubmitSafetyNet(): void {
    this.submitQueue.drainStaleViaBeacon();
    window.addEventListener('pagehide', () => {
      this.submitQueue.drainAllViaBeacon();
    });
  }

  @HostListener('document:click')
  @HostListener('document:touchend')
  async onFirstUserGesture(): Promise<void> {
    if (this.storageAccess.granted()) return;
    const granted = await this.storageAccess.ensure();
    if (granted) this.state.refresh().subscribe();
  }

  private setupBreakpointObserver() {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(
        map((result) => result.matches),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((isMobile) => {
        this.session.setIsMobile(isMobile);
      });
  }

  private removeGlobalLoader(): void {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('global-loader');
    if (el) {
      el.remove();
    }
  }
}
